import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarRange, LineChart, RefreshCw } from 'lucide-react';

import { assumptionsService } from '@/services/demandPlanning/assumptions.service';
import { planningCyclesService } from '@/services/demandPlanning/planningCycles.service';
import { scenariosService } from '@/services/demandPlanning/scenarios.service';

import {
  consensusOverviewQueryKeys,
  useConsensusOverviewQueries,
} from '../hooks/useConsensusOverviewQueries';
import { useScopeFilterOptions } from '../hooks/useScopeFilterOptions';

import { useWarehouseId } from '@/contexts/WarehouseContext';

import { ChartLegendHelp } from '../components/ChartLegendHelp';
import { AssumptionsPanel } from '../components/AssumptionsPanel';
import {
  EMPTY_ENTITY_SCOPE_FILTERS,
  type EntityScopeFilters,
} from '../components/ConsensusScopeFilters';
import { EntityScopeFilterBar } from '../components/EntityScopeFilterBar';
import { sopEntityScopeFilterBarOptions } from '../utils/sopScopeFilterBarOptions';
import { ConsensusDemandChart } from '../components/ConsensusDemandChart';
import { HelpTip, LabelWithHelp } from '../components/HelpTip';
import { SectionCard } from '../components/SectionCard';
import { DemandPlanPublishPanel } from '../components/DemandPlanPublishPanel';
import { PublishDemandPlanProgressModal } from '../components/PublishDemandPlanProgressModal';
import { ConsensusScenarioGrid } from '../components/ConsensusScenarioGrid';
import { usePublishDemandPlanFlow } from '../hooks/usePublishDemandPlanFlow';
import type {
  ConsensusChartHistoryRange,
  ConsensusOverviewPayload,
  ConsensusDemandPoint,
  ConsensusScenarioComparison,
  CreateAssumptionPayload,
  CreateScenarioPayload,
  DemandPlanStatus,
  UpdateAssumptionPayload,
} from '../types/consensus.types';
import type { ChartGranularity } from '../utils/chartLabels';
import { demandPlanningPageClass } from '../utils/pageShell';
import { cycleStatusBadgeClass, planningCycleStatusLabel } from '../utils/planningCycleStatus';

function resolveDemandPlanSeries(payload: ConsensusOverviewPayload | undefined): ConsensusDemandPoint[] {
  if (!payload?.demand_plan_chart?.series?.length) return [];
  return payload.demand_plan_chart.series;
}

const EMPTY_DEMAND_PLAN: DemandPlanStatus = {
  is_published: false,
  published_at: null,
  selected_scenario_id: null,
  selected_scenario_name: null,
  source: 'base',
};

const EMPTY_SCENARIO_COMPARISON: ConsensusScenarioComparison = {
  available_scenarios: [],
  macro_columns: [],
  macro_periods: [],
  periods: [],
  base_row: {
    scenario_id: null,
    scenario_name: '',
    scenario_type: 'base',
    status: 'approved',
    cells: [],
    macro_cells: [],
  },
  scenarios: [],
};

function parseCycleId(raw: string | null): number | null {
  if (!raw) return null;
  const id = Number.parseInt(raw, 10);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function ConsensusPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [historyRange, setHistoryRange] = useState<ConsensusChartHistoryRange>('52_weeks');
  const [granularity, setGranularity] = useState<ChartGranularity>('week');
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<number[]>([]);
  const [showScenarioForm, setShowScenarioForm] = useState(false);
  const [planEditMode, setPlanEditMode] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [entityFilters, setEntityFilters] = useState<EntityScopeFilters>(EMPTY_ENTITY_SCOPE_FILTERS);

  const cycleFromUrl = parseCycleId(searchParams.get('cycle'));
  const planningCycles = useQuery({
    queryKey: ['planning-cycles-list'],
    queryFn: () => planningCyclesService.list(),
  });

  const cycles = planningCycles.data ?? [];

  useEffect(() => {
    if (!cycles.length) return;
    const urlMatch = cycleFromUrl != null && cycles.some((c) => c.id === cycleFromUrl);
    if (urlMatch) {
      setSelectedCycleId(cycleFromUrl);
      return;
    }
    if (selectedCycleId == null) {
      setSelectedCycleId(cycles[0].id);
    }
  }, [cycles, cycleFromUrl, selectedCycleId]);

  const selectCycle = (cycleId: number) => {
    setSelectedCycleId(cycleId);
    setSearchParams({ cycle: String(cycleId) }, { replace: true });
  };

  const invalidateChartAndAssumptions = () => {
    void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.chartPrefix });
    void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.metaPrefix });
  };

  const invalidateScenarios = () => {
    void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.scenariosPrefix });
  };

  const invalidateConsensusFull = () => {
    void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.allPrefix });
    void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.metaPrefix });
    void queryClient.invalidateQueries({ queryKey: ['planning-cycles-list'] });
  };

  const overview = useConsensusOverviewQueries(
    selectedCycleId,
    historyRange,
    granularity,
    entityFilters,
  );

  const scopeOptions = useScopeFilterOptions(
    selectedCycleId != null,
    overview.data?.plan_scope?.families,
  );

  const createAssumption = useMutation({
    mutationFn: (payload: CreateAssumptionPayload) =>
      assumptionsService.create(selectedCycleId!, payload),
    onSuccess: invalidateChartAndAssumptions,
  });

  const updateAssumption = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateAssumptionPayload }) =>
      assumptionsService.update(id, selectedCycleId!, payload),
    onSuccess: invalidateChartAndAssumptions,
  });

  const deleteAssumption = useMutation({
    mutationFn: (id: number) => assumptionsService.delete(id, selectedCycleId!),
    onSuccess: invalidateChartAndAssumptions,
  });

  const syncForecast = useMutation({
    mutationFn: () => planningCyclesService.syncForecastAndBootstrap(selectedCycleId!, { force: false }),
    onMutate: () => {
      setSyncError(null);
      setSyncMessage(null);
    },
    onSuccess: (data) => {
      setSyncMessage(
        t('demandPlanning.syncForecastSuccess', {
          forecastLines: data.forecast_lines_created,
          consensusLines: data.consensus_lines_created,
        }),
      );
      invalidateConsensusFull();
    },
    onError: (error: Error) => {
      const apiErr = error as Error & { status?: number };
      if (apiErr.status === 409) {
        setSyncError(t('demandPlanning.syncForecastInProgress'));
        return;
      }
      setSyncError(apiErr.message || t('demandPlanning.syncForecastError'));
    },
  });

  const createScenario = useMutation({
    mutationFn: async (payload: CreateScenarioPayload) => {
      if (selectedCycleId == null) throw new Error('No planning cycle');
      const versionId = overview.data?.plan_version.id;
      if (!versionId) throw new Error('No plan version');
      const created = await scenariosService.create({
        ...payload,
        planning_cycle: selectedCycleId,
        base_plan_version: versionId,
      });
      return created;
    },
    onSuccess: (created) => {
      setSelectedScenarioIds((ids) =>
        ids.includes(created.id) ? ids : [...ids, created.id],
      );
      setShowScenarioForm(false);
      invalidateScenarios();
      void queryClient.invalidateQueries({ queryKey: ['scenarios-list'] });
    },
  });

  const availableScenarioKey = useMemo(() => {
    const list = overview.data?.scenario_comparison?.available_scenarios ?? [];
    return list
      .map((s) => s.id)
      .sort((a, b) => a - b)
      .join(',');
  }, [overview.data?.scenario_comparison?.available_scenarios]);

  const warehouseId = useWarehouseId();

  const publishFlow = usePublishDemandPlanFlow({
    cycleId: selectedCycleId,
    warehouseId,
    onSuccess: () => {
      setPlanEditMode(false);
      invalidateConsensusFull();
    },
  });

  useEffect(() => {
    if (!overview.data?.demand_plan?.is_published) {
      setPlanEditMode(false);
    }
  }, [overview.data?.demand_plan?.is_published, selectedCycleId]);

  useEffect(() => {
    setEntityFilters(EMPTY_ENTITY_SCOPE_FILTERS);
  }, [selectedCycleId]);

  useEffect(() => {
    if (!selectedCycleId) {
      setSelectedScenarioIds([]);
      return;
    }
    const ids = availableScenarioKey
      ? availableScenarioKey.split(',').map((id) => Number.parseInt(id, 10))
      : [];
    if (ids.length === 0) {
      setSelectedScenarioIds([]);
      return;
    }
    setSelectedScenarioIds((prev) => {
      const valid = prev.filter((id) => ids.includes(id));
      return valid.length > 0 ? valid : ids;
    });
  }, [selectedCycleId, availableScenarioKey]);

  const chartSeries = useMemo(() => resolveDemandPlanSeries(overview.data), [overview.data]);
  const hasPlanData = chartSeries.some(
    (p) => p.baseline_qty != null || p.consensus_qty != null,
  );
  const cycle = overview.data?.planning_cycle;
  const version = overview.data?.plan_version;
  const assumptions = overview.data?.assumptions ?? [];
  const assumptionsImpactSummary = overview.data?.assumptions_impact_summary;
  const chartMeta = overview.data?.demand_plan_chart;
  const demandPlan = overview.data?.demand_plan ?? EMPTY_DEMAND_PLAN;

  const cyclesTabTo =
    selectedCycleId != null
      ? `/demand-planning/sop/cycles?cycle=${selectedCycleId}`
      : '/demand-planning/sop/cycles';

  return (
    <div className={`${demandPlanningPageClass(embedded)} pt-4 md:pt-5`}>
      {!embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-gray-900">{t('consensus.title')}</h1>
              <HelpTip>{t('consensus.help.demandChart')}</HelpTip>
            </div>
            <p className="mt-1 text-sm text-gray-600">{t('consensus.subtitle')}</p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              to={cyclesTabTo}
              className="inline-flex items-center gap-2 text-stocker-primary hover:underline"
            >
              <CalendarRange className="h-4 w-4" />
              {t('nav.planningCycles')}
            </Link>
            <Link
              to="/demand-planning/sop/scenarios"
              className="text-stocker-primary hover:underline"
            >
              {t('nav.scenarios')}
            </Link>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <LabelWithHelp
              label={t('consensus.planningCycle')}
              help={t('consensus.help.planningCycle')}
              className="font-medium text-gray-700"
            />
            <select
              className="rounded-md border border-gray-300 px-3 py-2 text-sm min-w-[14rem]"
              value={selectedCycleId ?? ''}
              onChange={(e) => selectCycle(Number(e.target.value))}
              disabled={!cycles.length}
            >
              {cycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({planningCycleStatusLabel(c.status, t)})
                </option>
              ))}
            </select>
          </label>

          {cycle ? (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cycleStatusBadgeClass(cycle.status)}`}
            >
              {planningCycleStatusLabel(cycle.status, t)}
            </span>
          ) : null}

          <div className="flex items-center gap-1">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md border border-stocker-primary px-3 py-2 text-sm font-medium text-stocker-primary hover:bg-stocker-primary/5 disabled:opacity-50"
              disabled={selectedCycleId == null || syncForecast.isPending}
              onClick={() => void syncForecast.mutate()}
            >
              <RefreshCw className={`h-4 w-4 ${syncForecast.isPending ? 'animate-spin' : ''}`} />
              {t('demandPlanning.syncForecastBootstrap')}
            </button>
            <HelpTip>{t('demandPlanning.help.syncBootstrap')}</HelpTip>
          </div>
        </div>

        {version ? (
          <p className="text-sm text-gray-500">
            {version.version_name} (v{version.version_number})
          </p>
        ) : null}
      </div>

      {overview.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('demandPlanning.backendError')}
        </div>
      ) : null}

      {syncError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {syncError}
        </div>
      ) : null}

      {syncMessage ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {syncMessage}
        </div>
      ) : null}

      {syncForecast.isPending ? (
        <p className="text-sm text-gray-600">{t('demandPlanning.syncForecastPending')}</p>
      ) : null}

      {selectedCycleId != null ? (
        <EntityScopeFilterBar
          value={entityFilters}
          onChange={setEntityFilters}
          {...sopEntityScopeFilterBarOptions(scopeOptions)}
        />
      ) : null}

      {selectedCycleId != null &&
      (createAssumption.isPending || updateAssumption.isPending || deleteAssumption.isPending) ? (
        <p className="text-sm text-gray-600">{t('demandPlanning.rollupAssumptionPending')}</p>
      ) : null}

      {selectedCycleId != null ? (
        <AssumptionsPanel
          rows={assumptions}
          impactSummary={assumptionsImpactSummary}
          planFamilies={overview.data?.plan_scope?.families}
          scopeOptions={scopeOptions}
          cycleTitle={
            version
              ? `${cycle?.name ?? ''} · ${version.version_name} (#${version.version_number})`
              : (cycle?.name ?? '')
          }
          loading={overview.isLoading}
          saving={createAssumption.isPending || updateAssumption.isPending}
          deleting={deleteAssumption.isPending}
          onCreate={async (payload) => {
            await createAssumption.mutateAsync(payload);
          }}
          onUpdate={async (id, payload) => {
            await updateAssumption.mutateAsync({ id, payload });
          }}
          onDelete={async (id) => {
            await deleteAssumption.mutateAsync(id);
          }}
        />
      ) : null}

      <SectionCard
        title={t('consensus.demandPlanTitle')}
        icon={<LineChart className="h-4 w-4" />}
        help={
          <span className="block space-y-2">
            <span>{t('consensus.help.demandChart')}</span>
            <span className="text-gray-500">{t('consensus.demandPlanHint')}</span>
          </span>
        }
        headerExtra={
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              <LabelWithHelp
                label={t('consensus.chartRange')}
                help={t('consensus.help.historyRange')}
              />
              <select
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                value={historyRange}
                onChange={(e) => setHistoryRange(e.target.value as ConsensusChartHistoryRange)}
              >
                <option value="52_weeks">{t('consensus.range52Weeks')}</option>
                <option value="ytd">{t('consensus.rangeYtd')}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              <LabelWithHelp
                label={t('consensus.chartGranularity')}
                help={t('consensus.help.granularity')}
              />
              <select
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                value={granularity}
                onChange={(e) => setGranularity(e.target.value as ChartGranularity)}
              >
                <option value="week">{t('consensus.granularityWeek')}</option>
                <option value="month">{t('consensus.granularityMonth')}</option>
              </select>
            </label>
          </div>
        }
      >
        {overview.isLoading ? (
          <p className="text-sm text-gray-500 py-16 text-center">{t('common.loading')}</p>
        ) : !hasPlanData ? (
          <p className="text-sm text-gray-500 py-16 text-center">{t('consensus.noPlanData')}</p>
        ) : (
          <div className={overview.chartQuery.isFetching ? 'opacity-60 transition-opacity' : ''}>
            <ConsensusDemandChart
              data={chartSeries}
              currentPeriod={chartMeta?.current_period ?? null}
              granularity={(chartMeta?.granularity as ChartGranularity) ?? granularity}
              height={granularity === 'week' ? 400 : 360}
            />
            <ChartLegendHelp i18nPrefix="consensus.chartLegend" />
            {overview.chartQuery.isFetching ? (
              <p className="mt-2 text-center text-xs text-gray-500">{t('common.loading')}</p>
            ) : null}
          </div>
        )}
      </SectionCard>

      <ConsensusScenarioGrid
        comparison={overview.data?.scenario_comparison ?? EMPTY_SCENARIO_COMPARISON}
        loading={overview.isLoadingScenarios}
        selectedScenarioIds={selectedScenarioIds}
        onSelectedScenarioIdsChange={setSelectedScenarioIds}
        showCreateForm={showScenarioForm}
        creatingScenario={createScenario.isPending}
        onToggleCreateForm={() => setShowScenarioForm((v) => !v)}
        onCreateScenario={async (payload) => {
          await createScenario.mutateAsync(payload);
        }}
      />

      {cycle ? (
        <DemandPlanPublishPanel
          cycleId={cycle.id}
          demandPlan={demandPlan}
          availableScenarios={
            overview.data?.scenario_comparison?.available_scenarios ?? []
          }
          publishDisabled={publishFlow.isRunning}
          planEditMode={planEditMode}
          onPlanEditModeChange={setPlanEditMode}
          onRequestPublish={(scenarioId, republish, scenarioName) => {
            publishFlow.requestPublish({ scenarioId, republish, scenarioName });
          }}
        />
      ) : null}

      <PublishDemandPlanProgressModal
        open={publishFlow.modalOpen}
        mode={publishFlow.modalMode}
        request={publishFlow.pendingRequest}
        steps={publishFlow.steps}
        isRunning={publishFlow.isRunning}
        flowComplete={publishFlow.flowComplete}
        partialError={publishFlow.partialError}
        publishSucceeded={publishFlow.publishSucceeded}
        onConfirm={() => void publishFlow.confirmPublish()}
        onClose={publishFlow.closeModal}
        onRetryReplenishment={() => void publishFlow.retryReplenishment()}
      />
    </div>
  );
}
